import FeatureGates from '@atlaskit/feature-gate-js-client';
import { STATSIG_FEATURE_GATES } from '../bootstrap/statsigFeatureGates';
import { getExperiment, getExperimentNoExposure, manuallyLogExposure } from './experimentClient';
import { logError } from '../utils/logger';

export enum EXPERIMENTS {
	NTP_SEARCH_FILTERS = 'ntp_search_filters',
	NTP_SEARCH_FILTERS_3P_CONNECTORS = 'ntp_search_filters_3p_connectors',
	NTP_AGENTS_DROPDOWN = 'rovo_extension_ntp_agents_dropdown',
	DEFAULT_TO_CUSTOM_LINKS = 'rovo_extension_default_to_custom_links',

	NTP_NOTIFICATIONS_BELL = 'rovo-extension-ntp-notifications-bell',
	GOOGLE_SEARCH_TOGGLE = 'rovo_extension_google_search_toggle',

	ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY = 'rovo-ext_pcm_highlight_entry',
	// Immutable Switcheroo key — intentionally not `rovo-ext_pcm_*`. Do not rename.
	ROVO_CONTEXT_MENU_RIGHT_CLICK_MENU = 'rovo_extension_right_click_menu',
	ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR = 'rovo-ext_pcm_search_behavior',
	SETTINGS_DATA_MIGRATION = 'rovo-ext_settings_data_migration',
	MIGRATE_USER_PROFILE_CARD = 'pt_user_profile_card_migration_exp',
}

export enum PARAMETERS {
	cohort = 'cohort',
	isEnabled = 'enabled',
	isNtpSearchFiltersEnabled = 'is_search_filters_enabled',
	is3pConnectorEnabled = 'is3pConnectorEnabled',
	ntpAgentsDropdownTreatment = 'treatment',
	isSettingsUiRevampEnabled = 'is_settings_ui_revamp_enabled',
	// oxlint-disable-next-line typescript/no-duplicate-enum-values
	isDefaultToCustomLinksEnabled = 'isEnabled',

	isNotificationsEnabled = 'notificationsEnabled',
	// oxlint-disable-next-line typescript/no-duplicate-enum-values
	isGoogleSearchToggleEnabled = 'isEnabled',

	variant = 'variant',
	siteSettingsReadSource = 'siteSettingsReadSource',
	isUserProfileCardMigrationEnabled = 'isEnabled',
}

export const activeExperiments = [
	{
		experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS,
		parameterName: PARAMETERS.isNtpSearchFiltersEnabled,
	},
	{
		experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS_3P_CONNECTORS,
		parameterName: PARAMETERS.is3pConnectorEnabled,
	},
	{
		experimentName: EXPERIMENTS.NTP_AGENTS_DROPDOWN,
		parameterName: PARAMETERS.ntpAgentsDropdownTreatment,
	},
	{
		experimentName: EXPERIMENTS.DEFAULT_TO_CUSTOM_LINKS,
		parameterName: PARAMETERS.isDefaultToCustomLinksEnabled,
	},

	{
		experimentName: EXPERIMENTS.NTP_NOTIFICATIONS_BELL,
		parameterName: PARAMETERS.isNotificationsEnabled,
	},
	{
		experimentName: EXPERIMENTS.GOOGLE_SEARCH_TOGGLE,
		parameterName: PARAMETERS.isGoogleSearchToggleEnabled,
	},
	{
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY,
		parameterName: PARAMETERS.variant,
	},
	{
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_RIGHT_CLICK_MENU,
		parameterName: PARAMETERS.variant,
	},
	{
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR,
		parameterName: PARAMETERS.variant,
	},
	{
		experimentName: EXPERIMENTS.SETTINGS_DATA_MIGRATION,
		parameterName: PARAMETERS.siteSettingsReadSource,
	},
	{
		experimentName: EXPERIMENTS.MIGRATE_USER_PROFILE_CARD,
		parameterName: PARAMETERS.isUserProfileCardMigrationEnabled,
	},
];

export const isNtpSearchFiltersEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS,
		parameterName: PARAMETERS.isNtpSearchFiltersEnabled,
		defaultValue: false,
	});
};

export const isNtpSearchFilters3pConnectorsEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS_3P_CONNECTORS,
		parameterName: PARAMETERS.is3pConnectorEnabled,
		defaultValue: false,
	});
};

export type NtpAgentsDropdownTreatment = 'control' | 'A';
/**
 * - "control": Feature disabled
 * - "A": Show dropdown on Chat tab
 */
export const getNtpAgentsDropdownTreatment = (): NtpAgentsDropdownTreatment => {
	return getExperiment({
		experimentName: EXPERIMENTS.NTP_AGENTS_DROPDOWN,
		parameterName: PARAMETERS.ntpAgentsDropdownTreatment,
		defaultValue: 'control',
	}) as NtpAgentsDropdownTreatment;
};

// https://switcheroo.atlassian.com/ui/gates/6d883a6f-d1ae-451d-a056-51498e1d36b4/key/rovo_extension_default_to_custom_links
// Uses getExperiment (standard exposure) — fires exposure when Shortcuts component renders.
export const isDefaultToCustomLinksEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.DEFAULT_TO_CUSTOM_LINKS,
		parameterName: PARAMETERS.isDefaultToCustomLinksEnabled,
		defaultValue: false,
	});
};

/**
 * Session replay feature gate (Statsig). New recording starts when this gate is enabled — see `SessionReplayInitializer`.
 * While the same Gas browser session id is active, replay is not torn down when the gate becomes false;
 * when Gas mints a new session id, `onAnalyticsWebClientSessionRotation` stops replay if no longer eligible.
 *
 * The experiment `rovo-extension-session-replay` has been replaced by the feature gate
 * `rovo-extension-session-replay-gate`. Use this function which now checks the feature gate instead.
 */
export const isSessionReplayEnabled = (): boolean => {
	return FeatureGates.checkGate(STATSIG_FEATURE_GATES.SESSION_REPLAY);
};

export const isNtpNotificationsBellEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.NTP_NOTIFICATIONS_BELL,
		parameterName: PARAMETERS.isNotificationsEnabled,
		defaultValue: false,
	});
};

/**
 * Whether the user should see the Google tab toggle alongside the Search and Chat tabs
 * on the new tab page.
 */
export const isGoogleSearchToggleEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.GOOGLE_SEARCH_TOGGLE,
		parameterName: PARAMETERS.isGoogleSearchToggleEnabled,
		defaultValue: false,
	});
};

export const isConsentIntegrationEnabled = (): boolean => {
	return FeatureGates.checkGate(STATSIG_FEATURE_GATES.CONSENT);
};

/**
 * Whether the new user profile card (`UserProfileCardWithTrigger`) should be used for customers.
 *
 * Note: dogfooding is gated separately by the `people-teams_migrate-user-profile-card` feature
 * gate. Call sites should render the new card when the feature gate is on OR this experiment is on.
 */
export const isUserProfileCardMigrationEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.MIGRATE_USER_PROFILE_CARD,
		parameterName: PARAMETERS.isUserProfileCardMigrationEnabled,
		defaultValue: false,
	});
};

export type RovoContextMenuHighlightEntryVariant = 'control' | 'A' | 'B' | 'C';
export type RovoContextMenuRightClickVariant = 'control' | 'A' | 'B';
export type RovoContextMenuSearchBehaviorVariant = 'control' | 'A' | 'B';

const isRovoContextMenuHighlightEntryVariant = (
	value: unknown,
): value is RovoContextMenuHighlightEntryVariant =>
	value === 'control' || value === 'A' || value === 'B' || value === 'C';

const isRovoContextMenuRightClickVariant = (
	value: unknown,
): value is RovoContextMenuRightClickVariant =>
	value === 'control' || value === 'A' || value === 'B';

const isRovoContextMenuSearchBehaviorVariant = (
	value: unknown,
): value is RovoContextMenuSearchBehaviorVariant =>
	value === 'control' || value === 'A' || value === 'B';

export const getRovoContextMenuHighlightEntryVariant = (): RovoContextMenuHighlightEntryVariant => {
	const value = getExperiment<RovoContextMenuHighlightEntryVariant>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	return isRovoContextMenuHighlightEntryVariant(value) ? value : 'control';
};

/**
 * Whether the user is in a non-control cohort of the proactive context menu
 * highlight-entry experiment.
 *
 * Reads the experiment WITHOUT firing exposure. This gates browser-context
 * plumbing in chat (the context banner, and dropping `htmlBody` when the admin
 * has DOM content disabled) rather than the highlight surface itself, so it is
 * not a meaningful allocation point. Exposure stays owned by the highlight
 * surface via {@link getRovoContextMenuHighlightEntryVariant}.
 */
export const isRovoContextMenuHighlightEntryEnabledNoExposure = (): boolean => {
	const value = getExperimentNoExposure<RovoContextMenuHighlightEntryVariant>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});

	return isRovoContextMenuHighlightEntryVariant(value) && value !== 'control';
};

export const getRovoContextMenuRightClickVariant = (): RovoContextMenuRightClickVariant => {
	const value = getExperiment<RovoContextMenuRightClickVariant>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_RIGHT_CLICK_MENU,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	return isRovoContextMenuRightClickVariant(value) ? value : 'control';
};

export const getRovoContextMenuSearchBehaviorVariant = (): RovoContextMenuSearchBehaviorVariant => {
	const value = getExperiment<RovoContextMenuSearchBehaviorVariant>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	return isRovoContextMenuSearchBehaviorVariant(value) ? value : 'control';
};

export const getAllRovoContextMenuVariants = (): {
	highlightEntry: RovoContextMenuHighlightEntryVariant;
	rightClickMenu: RovoContextMenuRightClickVariant;
	searchBehavior: RovoContextMenuSearchBehaviorVariant;
} => ({
	highlightEntry: getRovoContextMenuHighlightEntryVariant(),
	rightClickMenu: getRovoContextMenuRightClickVariant(),
	searchBehavior: getRovoContextMenuSearchBehaviorVariant(),
});

export const deriveIsRovoContextMenuHighlightFabEnabled = (
	variant: RovoContextMenuHighlightEntryVariant,
): boolean => variant === 'A';

export const deriveIsRovoContextMenuHighlightMenuDirect = (
	variant: RovoContextMenuHighlightEntryVariant,
): boolean => variant === 'B' || variant === 'C';

export const deriveIsRovoContextMenuHighlightOneLineMenuEnabled = (
	variant: RovoContextMenuHighlightEntryVariant,
): boolean => variant === 'C';

export const deriveIsRovoContextMenuRightClickFabEnabled = (
	variant: RovoContextMenuRightClickVariant,
): boolean => variant === 'A' || variant === 'B';

export const deriveIsRovoContextMenuRightClickOneLineMenuEnabled = (
	variant: RovoContextMenuRightClickVariant,
): boolean => variant === 'B';

export const deriveIsRovoContextMenuSearchInSidePanelEnabled = (
	variant: RovoContextMenuSearchBehaviorVariant,
): boolean => variant === 'A';

export const deriveIsRovoContextMenuSearchFullPageEnabled = (
	variant: RovoContextMenuSearchBehaviorVariant,
): boolean => variant === 'B';

export const deriveIsRovoContextMenuEnabled = (
	highlightEntry: RovoContextMenuHighlightEntryVariant,
	rightClickMenu: RovoContextMenuRightClickVariant,
	searchBehavior: RovoContextMenuSearchBehaviorVariant,
): boolean =>
	highlightEntry !== 'control' || rightClickMenu !== 'control' || searchBehavior !== 'control';

export const isRovoContextMenuHighlightFabEnabled = (): boolean =>
	deriveIsRovoContextMenuHighlightFabEnabled(getRovoContextMenuHighlightEntryVariant());

export const isRovoContextMenuHighlightMenuDirect = (): boolean =>
	deriveIsRovoContextMenuHighlightMenuDirect(getRovoContextMenuHighlightEntryVariant());

export const isRovoContextMenuHighlightOneLineMenuEnabled = (): boolean =>
	deriveIsRovoContextMenuHighlightOneLineMenuEnabled(getRovoContextMenuHighlightEntryVariant());

export const isRovoContextMenuRightClickFabEnabled = (): boolean =>
	deriveIsRovoContextMenuRightClickFabEnabled(getRovoContextMenuRightClickVariant());

export const isRovoContextMenuRightClickOneLineMenuEnabled = (): boolean =>
	deriveIsRovoContextMenuRightClickOneLineMenuEnabled(getRovoContextMenuRightClickVariant());

export const isRovoContextMenuSearchInSidePanelEnabled = (): boolean =>
	deriveIsRovoContextMenuSearchInSidePanelEnabled(getRovoContextMenuSearchBehaviorVariant());

export const isRovoContextMenuSearchFullPageEnabled = (): boolean =>
	deriveIsRovoContextMenuSearchFullPageEnabled(getRovoContextMenuSearchBehaviorVariant());

export const isRovoContextMenuEnabled = (): boolean => {
	const { highlightEntry, rightClickMenu, searchBehavior } = getAllRovoContextMenuVariants();
	return deriveIsRovoContextMenuEnabled(highlightEntry, rightClickMenu, searchBehavior);
};

export const isRovoContextMenuEnabledNoExposure = (): boolean => {
	const highlightEntryRaw = getExperimentNoExposure<string>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	const rightClickMenuRaw = getExperimentNoExposure<string>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_RIGHT_CLICK_MENU,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	const searchBehaviorRaw = getExperimentNoExposure<string>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	const highlightEntry = isRovoContextMenuHighlightEntryVariant(highlightEntryRaw)
		? highlightEntryRaw
		: 'control';
	const rightClickMenu = isRovoContextMenuRightClickVariant(rightClickMenuRaw)
		? rightClickMenuRaw
		: 'control';
	const searchBehavior = isRovoContextMenuSearchBehaviorVariant(searchBehaviorRaw)
		? searchBehaviorRaw
		: 'control';
	return deriveIsRovoContextMenuEnabled(highlightEntry, rightClickMenu, searchBehavior);
};

export type SiteSettingsReadSource = 'legacy' | 'rovoextadminsettings';

/**
 * Returns `true` when site settings (company announcements / theming) should be
 * read from the consolidated `rovoextadminsettings` source, and `false` when the
 * legacy per-feature settings should be used.
 *
 * This reads the experiment WITHOUT firing exposure. The allocation point (this
 * routing decision) is evaluated on multiple hot paths — every settings read and
 * every double-write, sometimes more than once per user request — so firing
 * exposure here would inflate exposure counts. Exposure should instead be logged
 * once at the meaningful point via `exposeUserToSettingsDataMigrationExperiment`.
 */
export const isSiteSettingsReadFromAdminSettings = (): boolean => {
	const readSource = getExperimentNoExposure({
		experimentName: EXPERIMENTS.SETTINGS_DATA_MIGRATION,
		parameterName: PARAMETERS.siteSettingsReadSource,
		defaultValue: 'legacy',
	});

	return readSource === 'rovoextadminsettings';
};

// Guards against firing more than one exposure for the settings data-migration
// experiment per page/session. A single settings page commonly reads both
// announcements and theming, and each read is a legitimate exposure point, so we
// dedupe here rather than trying to pick a single "owning" read.
let hasLoggedSettingsDataMigrationExposure = false;

/**
 * Logs exposure for the settings data-migration experiment, at most once per
 * page/session. Safe to call from every user-facing settings read (e.g. both the
 * announcements and theming reads) — subsequent calls are no-ops. This is
 * decoupled from {@link isSiteSettingsReadFromAdminSettings}, which reads the
 * experiment without firing exposure.
 */
export const exposeUserToSettingsDataMigrationExperiment = (): void => {
	if (hasLoggedSettingsDataMigrationExposure) {
		return;
	}

	/*
	 * Exposure fires from render effects, which can run before Statsig has
	 * initialised. Until it has, the read source resolves to the control default,
	 * so recording an exposure would misattribute the user. Skip without latching
	 * the flag and let a later mount record it instead.
	 */
	if (!FeatureGates.initializeCompleted()) {
		return;
	}

	try {
		manuallyLogExposure(EXPERIMENTS.SETTINGS_DATA_MIGRATION);
		hasLoggedSettingsDataMigrationExposure = true;
	} catch (error) {
		logError({ error, impact: 'Failed to log settings data migration experiment exposure' });
	}
};
